/* ============================================================================
   Role permissions. The studio manager can do everything; every team member
   carries a level (admin / contractor / consultant / technician / viewer —
   clients live in the curated portal instead). One matrix drives both the UI
   gates and the "Permissions by role" table on Team & Invites, so what the
   table says is literally what the code enforces.
   ========================================================================== */
import type { Session } from "./auth";
import type { TeamRole, CustomRole, RoleOverrides } from "./types";
import { isBuiltInRole } from "./types";
// pmStore holds the project's own roles. It doesn't import this module, so
// there's no cycle — the dependency runs one way only.
import { getProjects } from "./pmStore";
import { isStudioAdmin, isCommsManager } from "./studioRoles";

export type Capability =
  | "admin-sections"
  | "schedule-meetings"
  | "add-agenda"
  | "raise-rfis"
  | "answer-rfis"
  | "file-daily-reports"
  | "acknowledge-reports"
  | "request-materials"
  | "advance-materials"
  | "add-observations"
  | "respond-observations"
  | "record-site-visits"
  | "upload-drawings"
  | "import-programme"
  | "manage-targets"
  | "set-goals"
  | "add-photos"
  | "manage-renders"
  | "issue-reports"
  | "review-punch"
  | "manage-correspondence";

/* The communications manager coordinates every project's conversations and
   nothing else: RFIs, correspondence, meetings, observations — never the
   drawings, the programme, the money or the approvals. */
const COMMS_CAPS = new Set<Capability>([
  "schedule-meetings", "add-agenda", "raise-rfis", "answer-rfis", "add-observations",
  "record-site-visits", "manage-correspondence",
]);

type MatrixRole = Exclude<TeamRole, "client">;
const R = (admin: boolean, contractor: boolean, consultant: boolean, technician: boolean, viewer: boolean): Record<MatrixRole, boolean> =>
  ({ admin, contractor, consultant, technician, viewer });

export const PERMISSIONS: { cap: Capability; label: string; grants: Record<MatrixRole, boolean> }[] = [
  { cap: "admin-sections",      label: "Full access — all projects & switcher, Team & Invites, Client Portal control", grants: R(true, false, false, false, false) },
  { cap: "schedule-meetings",   label: "Schedule, run & minute meetings",        grants: R(true,  false, false, false, false) },
  { cap: "add-agenda",          label: "Add meeting agenda points",              grants: R(true,  true,  true,  true,  false) },
  { cap: "raise-rfis",          label: "Raise RFIs",                             grants: R(true,  true,  true,  true,  false) },
  { cap: "answer-rfis",         label: "Answer RFIs",                            grants: R(true,  false, false, false, false) },
  { cap: "file-daily-reports",  label: "File daily site reports",                grants: R(true,  true,  false, true,  false) },
  { cap: "acknowledge-reports", label: "Acknowledge daily reports",              grants: R(true,  false, false, false, false) },
  { cap: "request-materials",   label: "Request materials",                      grants: R(true,  true,  false, true,  false) },
  { cap: "advance-materials",   label: "Review / approve / order materials",     grants: R(true,  false, false, false, false) },
  { cap: "add-observations",    label: "Log observations",                       grants: R(true,  true,  true,  true,  false) },
  { cap: "respond-observations",label: "Respond to & close observations",        grants: R(true,  false, false, false, false) },
  { cap: "record-site-visits",  label: "Record site-visit reports",              grants: R(true,  false, true,  false, false) },
  { cap: "upload-drawings",     label: "Upload drawings (auto-revision)",        grants: R(true,  true,  true,  false, false) },
  { cap: "import-programme",    label: "Import / update the work programme",     grants: R(true,  true,  false, false, false) },
  { cap: "manage-targets",      label: "Set & tick daily targets",               grants: R(true,  true,  false, true,  false) },
  { cap: "set-goals",           label: "Set the week's goals for the contractor", grants: R(true,  false, false, false, false) },
  { cap: "add-photos",          label: "Add site photos",                        grants: R(true,  true,  true,  true,  false) },
  { cap: "manage-renders",      label: "Upload renders & set the main render",   grants: R(true,  false, false, false, false) },
  { cap: "issue-reports",       label: "Issue progress reports",                 grants: R(true,  false, false, false, false) },
  { cap: "review-punch",        label: "Close / reopen punch items",             grants: R(true,  false, false, false, false) },
  { cap: "manage-correspondence", label: "Issue correspondence & send email with attachments", grants: R(true, false, false, false, false) },
];

/* ---- the project's own roles -------------------------------------------- */
/* A project may define roles of its own and may change what the built-in ones
   can do. Resolution order is: the project's custom role, then the project's
   override of a built-in role, then the default matrix above. Read lazily so
   this module stays usable before any project is loaded. */

function projectOf(s: Session | null) {
  if (!s || s.role !== "contractor") return null;
  return getProjects().find((p) => p.id === s.projectId) ?? null;
}

/** The role a session is acting under, whatever kind it is. */
export function roleKeyOf(s: Session | null): string | null {
  if (!s) return null;
  if (s.role === "manager") return "manager";
  if (s.role === "pclient") return "client";
  if (s.role === "contractor") return s.teamRole ?? "contractor";
  return null;
}

/** The acting level of the current session inside the PM module. Custom roles
    report as their own id — callers that need a matrix column use `can`. */
export function levelOf(s: Session | null): "manager" | MatrixRole | "client" | null {
  const key = roleKeyOf(s);
  if (!key) return null;
  if (key === "manager") return "manager";
  if (key === "client") return "client";
  if (isBuiltInRole(key)) return key === "client" ? "client" : (key as MatrixRole);
  // a custom role has no matrix column; treat it as its own thing
  return null;
}

export function can(s: Session | null, cap: Capability): boolean {
  const key = roleKeyOf(s);
  if (key === "manager") return true;
  if (isCommsManager(s)) return COMMS_CAPS.has(cap);
  if (!key || key === "client") return false;

  const p = projectOf(s);
  // a role the project defined for itself
  const custom = p?.customRoles?.find((r) => r.id === key);
  if (custom) return !!custom.grants[cap];
  // a built-in role the project has re-ticked
  const override = p?.roleOverrides?.[key]?.[cap];
  if (typeof override === "boolean") return override;

  if (!isBuiltInRole(key) || key === "client") return false;
  return PERMISSIONS.find((r) => r.cap === cap)?.grants[key as MatrixRole] ?? false;
}

/** Full-access mode inside the Project Manager: the studio manager, Studio
    Admins and Site Admins work across ALL projects — project list, switcher,
    new projects. A custom role can be granted the same reach explicitly.
    (The studio account itself stays manager-only.) */
export function fullAccess(s: Session | null): boolean {
  if (s?.role === "manager") return true;
  // a Studio Admin carries a bid-shaped session but owns the project side too
  if (isStudioAdmin(s)) return true;
  if (s?.role !== "contractor") return false;
  if (s.teamRole === "admin") return true;
  const custom = projectOf(s)?.customRoles?.find((r) => r.id === s.teamRole);
  return !!custom?.fullAccess;
}

/** Every role a project can assign, built-in and its own, for the pickers. */
export function rolesFor(p: { customRoles?: CustomRole[] } | null | undefined): { id: string; label: string; custom: boolean }[] {
  return [
    ...(["admin", "contractor", "consultant", "technician", "client", "viewer"] as TeamRole[])
      .map((r) => ({ id: r, label: LEVEL_LABEL[r === "client" ? "client" : (r as MatrixRole)], custom: false })),
    ...(p?.customRoles ?? []).map((r) => ({ id: r.id, label: r.name, custom: true })),
  ];
}

/** What a role may do, resolved the same way `can` resolves it — this is what
    the editable matrix renders, so the table always equals the enforcement. */
export function grantsFor(p: { customRoles?: CustomRole[]; roleOverrides?: RoleOverrides } | null | undefined, roleId: string): Record<string, boolean> {
  const custom = p?.customRoles?.find((r) => r.id === roleId);
  if (custom) return { ...custom.grants };
  const out: Record<string, boolean> = {};
  for (const row of PERMISSIONS) {
    const override = p?.roleOverrides?.[roleId]?.[row.cap];
    out[row.cap] = typeof override === "boolean"
      ? override
      : (isBuiltInRole(roleId) && roleId !== "client" ? row.grants[roleId as MatrixRole] : false);
  }
  return out;
}

/** A role's display name, built-in or the project's own. */
export function roleLabel(p: { customRoles?: CustomRole[] } | null | undefined, roleId: string): string {
  const custom = p?.customRoles?.find((r) => r.id === roleId);
  if (custom) return custom.name;
  return (LEVEL_LABEL as Record<string, string>)[roleId] ?? roleId;
}

export const LEVEL_LABEL: Record<MatrixRole | "client", string> = {
  admin: "Site Admin",
  contractor: "Contractor View",
  consultant: "Consultant",
  technician: "Technician",
  viewer: "Viewer",
  client: "Client",
};
